import { useQuery } from '@tanstack/react-query'
import { Navigate, Outlet, useLocation } from 'react-router'
import { IconTile } from '@/components/brand/icon-tile'
import { Logo } from '@/components/brand/logo'
import { SoundBars } from '@/components/brand/scene'
import { ghostActionClass } from '@/components/shared/styles'
import { Button } from '@/components/ui/button'
import { sessionQuery } from '@/lib/api/queries'
import { cn } from '@/lib/utils'

/**
 * Route guard. The server is the authority — this only decides what to paint
 * while the session resolves, and where to send someone who has none.
 *
 * Three outcomes, deliberately kept apart. Collapsing "the API did not answer"
 * into "you are signed out" sends the user to a login page that cannot possibly
 * work, with nothing on screen explaining why — and signing in again just
 * repeats it. An unreachable API has to look unreachable.
 */
export function RequireSession() {
  const location = useLocation()
  const session = useQuery(sessionQuery)

  if (session.isPending) {
    return (
      <div
        role="status"
        aria-busy="true"
        aria-label="Loading your session"
        className="grid min-h-svh place-items-center bg-background"
      >
        <div className="flex flex-col items-center gap-5">
          <Logo variant="mark" className="size-10" />
          <SoundBars />
        </div>
      </div>
    )
  }

  // Could not determine — not the same as signed out. Never redirect on this.
  if (session.isError) {
    return (
      <div className="grid min-h-svh place-items-center bg-background p-4">
        <div className="w-full max-w-md rounded-[22px] border border-border bg-card p-6 shadow-card-lift sm:p-8">
          <div role="alert" className="flex items-start gap-4">
            {/* Warm: nothing is answering, so calls may be at risk. */}
            <IconTile icon="warn" tone="warm" size="lg" />
            <div className="min-w-0 pt-0.5">
              <h1 className="text-subtitle font-extrabold">Can’t reach RingOut</h1>
              <p className="mt-1.5 text-small text-muted-foreground">
                We couldn’t check whether you’re signed in. The API may be starting up or
                unreachable.
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            className={cn(ghostActionClass, 'mt-6 w-full')}
            onClick={() => void session.refetch()}
            disabled={session.isFetching}
          >
            {session.isFetching ? 'Retrying…' : 'Try again'}
          </Button>
        </div>
      </div>
    )
  }

  const next = location.pathname + location.search

  // Answered, and the answer is "nobody is signed in".
  if (!session.data) {
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />
  }

  // Signed in, address unproven. A wall, not a banner: `require_membership`
  // refuses every account-scoped read until the code is entered, so the
  // dashboard behind this would render as a console full of empty panels with
  // no explanation. `next` survives the detour, so verifying lands them where
  // they were going.
  if (!session.data.email_verified) {
    return <Navigate to={`/verify-email?next=${encodeURIComponent(next)}`} replace />
  }

  return <Outlet />
}
